import { SegmentDefinition } from '@fullstory/proto/fs/api/segment';
import {
  GeneratedSearchie,
  QueryCacheOptions,
  SearchieMatchingUsersSettings,
  SearchSessionsResponse,
  SearchUser,
} from '@fullstory/proto/fs/services/alexa';
import { Timestamp } from '@fullstory/proto/google/protobuf';
import { isDataFresh, optRefreshUserList } from '@fullstory/searchiecache';
import { isEqual } from 'lodash';
import { AppDispatch, AppThunkAction } from '../../types';
import { ActionType, createAction, createAppCacheActions } from '@fullstory/redux-util/utils';
import { createAppCacheRequestAction } from '@fullstory/redux-util/utils/appCache/actions';
import { GravatarThunks } from '../gravatars';
import { MatchingUsersRequestActions } from '../matchingUsers';
import { skipCacheOpts } from '../matchingUsers/utils';
import { SearchCacheKey } from '../searchMeta';
import { SearchSessionsRequestActions } from '../searchSessions';
import { WatchHistoryRequestActions } from '../watchHistory';
import {
  SessionPlaylistCardFilterActionTypes,
  SessionReplayTypes,
  UsersAndSessions,
} from './types';
import { UID } from '@fullstory/util/uid';
import { createRequestCancellationAction } from '@fullstory/redux-util';

export const SessionReplayActions = createAppCacheActions(
  SessionReplayTypes.UsersAppCacheIsLoading,
  SessionReplayTypes.UsersAppCacheLoadFailed,
  SessionReplayTypes.UsersAppCacheRefetching,
  SessionReplayTypes.UsersAppCacheHasData,
  SessionReplayTypes.UsersAppCacheFresh,
  SessionReplayTypes.UsersAppCacheSwap,
  SessionReplayTypes.UsersAppCacheClear,
)<UsersAndSessions>();

export type SessionReplayActions =
  | ActionType<typeof SessionReplayActions>
  | ActionType<typeof SessionReplayFilterActions>;

export type SessionReplayThunkAction<R> = AppThunkAction<R, SessionReplayActions>;
export interface SessionReplayPayload {
  segmentDefinition: SegmentDefinition;
  searchCacheKey: SearchCacheKey;
  settings: SearchieMatchingUsersSettings;
  sessionSegmentDefinitions: SegmentDefinition[] | undefined;
  page: number;
  orgId: string;
  storageId: string;
  followUpReq: boolean;
}

const extractCacheOptions = (payload: SessionReplayPayload, isRefetch: boolean) =>
  payload.page > 0 ? skipCacheOpts : isRefetch ? optRefreshUserList : null;

export const SessionReplayFilterActions = {
  setFunnelStep: (storageId: string, step: number) =>
    createAction({ type: SessionPlaylistCardFilterActionTypes.SetFunnelStep, storageId, step }),
  setFunnelConverted: (storageId: string, converted: string) =>
    createAction({
      type: SessionPlaylistCardFilterActionTypes.SetFunnelConverted,
      storageId,
      converted,
    }),
  setActiveSegmentId: (storageId: string, activeSegmentId: string) =>
    createAction({
      type: SessionPlaylistCardFilterActionTypes.SetActiveSegmentId,
      storageId,
      activeSegmentId,
    }),
  setDimension: (storageId: string, dimension: string | null) =>
    createAction({ type: SessionPlaylistCardFilterActionTypes.SetDimension, storageId, dimension }),
  setPage: (storageId: string, page: number, queryPage?: number) =>
    createAction({
      type: SessionPlaylistCardFilterActionTypes.SetPage,
      storageId,
      page,
      queryPage,
    }),
  setEventIndex: (storageId: string, eventIndex: number) =>
    createAction({
      type: SessionPlaylistCardFilterActionTypes.SetEventIndex,
      storageId,
      eventIndex,
    }),
  setReturnEventIndex: (storageId: string, returnEventIndex: number) =>
    createAction({
      type: SessionPlaylistCardFilterActionTypes.SetReturnEventIndex,
      storageId,
      returnEventIndex,
    }),
  setCohortIndex: (storageId: string, cohortIndex: number) =>
    createAction({
      type: SessionPlaylistCardFilterActionTypes.SetCohortIndex,
      storageId,
      cohortIndex,
    }),
  setReturnWindowIndex: (storageId: string, returnWindowIndex: number) =>
    createAction({
      type: SessionPlaylistCardFilterActionTypes.SetReturnWindowIndex,
      storageId,
      returnWindowIndex,
    }),
  setCompleteSessions: (storageId: string, completeSessions: boolean) =>
    createAction({
      type: SessionPlaylistCardFilterActionTypes.SetCompleteSessions,
      storageId,
      completeSessions,
    }),
  clearFilters: (storageId: string) =>
    createAction({ type: SessionPlaylistCardFilterActionTypes.ClearFilters, storageId }),
};

export const SessionAsyncActions = {
  appCache: (fetchGravatars: boolean, uid?: UID) => ({
    action: createAppCacheRequestAction<UsersAndSessions, SessionReplayPayload>()({
      query: (dispatch: AppDispatch, payload: SessionReplayPayload, isRefetch: boolean) => {
        const cacheOptions = extractCacheOptions(payload, isRefetch);
        return dispatch(
          SessionAsyncActions.fetchUsersAndSessions(
            payload.segmentDefinition,
            payload.searchCacheKey,
            payload.settings,
            payload.sessionSegmentDefinitions,
            payload.page,
            payload.orgId,
            cacheOptions ?? undefined,
            payload.followUpReq ?? false,
            isRefetch,
            fetchGravatars,
            uid,
          ),
        );
      },
      loading: SessionReplayActions.appCacheIsLoading,
      failed: SessionReplayActions.appCacheLoadFailed,
      refetching: SessionReplayActions.appCacheIsRefetching,
      hasData: SessionReplayActions.appCacheHasData,
      fresh: SessionReplayActions.appCacheFresh,
      isDataFresh: (payload: SessionReplayPayload, data: UsersAndSessions) =>
        isDataFresh(data.users.cacheOptions, Timestamp.mustDate(data.users.computedAt), new Date()),
      isSimiliar: (newData: UsersAndSessions, oldData: UsersAndSessions) =>
        similarData(oldData.users.generatedSearchie, newData.users.generatedSearchie),
    }),
    cancel: (dispatch: AppDispatch, uid: UID) => {
      dispatch(createRequestCancellationAction(uid));
    },
  }),

  /**
   * We need to always fetch the sessions with the knowledge of the users,
   * this thunk unifies those two requests into one logical request,
   * so that it can be treated as one within app cache
   */
  fetchUsersAndSessions:
    (
      segmentDefinition: SegmentDefinition,
      searchCacheKey: SearchCacheKey,
      settings: SearchieMatchingUsersSettings,
      sessionSegmentDefinitions: SegmentDefinition[] | undefined = [],
      page: number,
      orgId: string,
      cacheOptions: QueryCacheOptions | undefined,
      followUpReq: boolean,
      refresh: boolean,
      fetchGravatars: boolean,
      uid?: UID,
    ): SessionReplayThunkAction<Promise<UsersAndSessions>> =>
    async (dispatch: AppDispatch) => {
      const users = await dispatch(
        MatchingUsersRequestActions.fetchMatchingUsers(
          segmentDefinition,
          settings,
          orgId,
          followUpReq,
          refresh,
          page,
          searchCacheKey,
          cacheOptions,
          uid,
        ),
      );
      const searchUsers: SearchUser[] = users.generatedSearchie?.matchingUsersData?.users ?? [];
      // additional segment definition is only present in the case of I&I
      // if present, session segment definition should only contain the additional filters
      // More info: https://github.com/cowpaths/mn/pull/13631#issuecomment-509720366
      const payload =
        settings.eventSearch ??
        (sessionSegmentDefinitions?.length ? sessionSegmentDefinitions : [segmentDefinition]);

      const sessionPromise: Promise<{ [id: string]: SearchSessionsResponse }> = new Promise(
        resolve => {
          const promises = searchUsers
            .filter(u => u.fetchClips)
            .map(async ({ indvId }) => {
              const id = indvId as string;
              const data = await dispatch(
                SearchSessionsRequestActions.searchSessions(payload, id, orgId, {
                  maxOutgoingRequests: 3,
                  id: 'clipList',
                }),
              );
              return { id, data };
            });
          resolve(
            Promise.all(promises).then(responses =>
              responses.reduce<{ [id: string]: SearchSessionsResponse }>(
                (acc, { id, data }) => ({ ...acc, [id]: data }),
                {},
              ),
            ),
          );
        },
      );
      const gravatarPromise = fetchGravatars
        ? dispatch(
            GravatarThunks.fetchGravatars(
              users?.generatedSearchie?.matchingUsersData?.users.map(u => u.email) ?? [],
            ),
          )
        : Promise.resolve();
      const watchHistoryPromise = dispatch(WatchHistoryRequestActions.fetchWatchHistory());
      const [sessions] = await Promise.all([sessionPromise, gravatarPromise, watchHistoryPromise]);
      return {
        users,
        sessions,
      };
    },
};

function similarData(
  oldData: GeneratedSearchie | null,
  newData: GeneratedSearchie | null,
): boolean {
  if (!oldData || !newData) return false;
  return isEqual(newData, oldData);
}
